import React from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const STAGE_TYPES = [
  'eliminatoria', 'repescagem', 'oitavas', 'quartas', 'semifinal',
  'terceiro_lugar', 'final', 'especial',
];

function BandsCountToggle({ value, onChange }) {
  const btn = (count, label, subtitle, testId, onClick) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-md border px-3 py-2 text-sm text-left ${
        value === count
          ? 'border-[var(--g66-amber)] bg-[color:hsla(42,92%,52%,0.15)] text-white'
          : 'border-border text-muted-foreground hover:text-white'
      }`}
    >
      <div className="font-semibold">{label}</div>
      <div className="text-xs opacity-75">{subtitle}</div>
    </button>
  );

  return (
    <div className="sm:col-span-2 rounded-lg border border-[color:hsla(42,92%,52%,0.3)] bg-[color:hsla(42,92%,52%,0.05)] p-3">
      <Label className="text-white">Formato da noite</Label>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {btn(2, '2 bandas', 'Semifinal, Final, 3º lugar', 'stage-form-2-bands', () => onChange(2))}
        {btn(3, '3 bandas', 'Eliminatórias, Quartas de final', 'stage-form-3-bands', () => onChange(3))}
      </div>
    </div>
  );
}

function BandSelector({ label, bands, value, onValueChange, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select
        value={value || 'none'}
        onValueChange={(v) => onValueChange(v === 'none' ? '' : v)}
      >
        <SelectTrigger className="mt-1" data-testid={testId}><SelectValue placeholder="Selecionar" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">—</SelectItem>
          {bands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Modal to create or edit a stage. Owns only the presentational form UI —
 * data fetching / persistence stays in the parent (AdminStages).
 */
export function StageFormDialog({ open, editing, form, setForm, bands, saving, onSave, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {editing ? 'Editar etapa' : 'Nova etapa'}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label>Título*</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1"
              data-testid="stage-form-title"
            />
          </div>
          <div>
            <Label>Tipo</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STAGE_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Data*</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="mt-1"
              data-testid="stage-form-date"
            />
          </div>
          <div>
            <Label>Hora de início</Label>
            <Input
              type="time"
              value={form.start_time}
              onChange={(e) => setForm({ ...form, start_time: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Hora prevista da votação</Label>
            <Input
              type="time"
              value={form.voting_planned_time}
              onChange={(e) => setForm({ ...form, voting_planned_time: e.target.value })}
              className="mt-1"
            />
          </div>

          <BandsCountToggle
            value={form.bands_count}
            onChange={(count) => setForm({
              ...form,
              bands_count: count,
              band_c_id: count === 3 ? form.band_c_id : '',
            })}
          />

          <BandSelector
            label="Banda A"
            bands={bands}
            value={form.band_a_id}
            onValueChange={(v) => setForm({ ...form, band_a_id: v })}
            testId="stage-form-band-a"
          />
          <BandSelector
            label="Banda B"
            bands={bands}
            value={form.band_b_id}
            onValueChange={(v) => setForm({ ...form, band_b_id: v })}
            testId="stage-form-band-b"
          />
          {form.bands_count === 3 && (
            <div className="sm:col-span-2">
              <BandSelector
                label="Banda C"
                bands={bands}
                value={form.band_c_id}
                onValueChange={(v) => setForm({ ...form, band_c_id: v })}
                testId="stage-form-band-c"
              />
            </div>
          )}

          <div className="sm:col-span-2 flex items-center gap-3 rounded-lg border border-border p-3">
            <Switch
              checked={form.require_night_password}
              onCheckedChange={(v) => setForm({ ...form, require_night_password: v })}
            />
            <div className="flex-1">
              <div className="text-sm text-white">Exigir senha da noite</div>
              <div className="text-xs text-muted-foreground">Adicione uma senha anunciada no Pub para dificultar votos fora do local.</div>
            </div>
          </div>
          {form.require_night_password && (
            <div className="sm:col-span-2">
              <Label>Senha da noite</Label>
              <Input
                value={form.night_password}
                onChange={(e) => setForm({ ...form, night_password: e.target.value })}
                className="mt-1 font-mono"
                placeholder="Ex: GARAGE66"
              />
            </div>
          )}

          <div className="sm:col-span-2">
            <Label>Observações públicas</Label>
            <Textarea
              rows={2}
              value={form.public_notes}
              onChange={(e) => setForm({ ...form, public_notes: e.target.value })}
              className="mt-1"
            />
          </div>
          <div className="sm:col-span-2">
            <Label>Observações internas</Label>
            <Textarea
              rows={2}
              value={form.internal_notes}
              onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
              className="mt-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            data-testid="stage-form-submit"
            onClick={onSave}
            disabled={saving}
            className="bg-[var(--g66-amber)] text-black hover:bg-[var(--g66-amber-2)]"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
